import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CDropdown,
  CDropdownDivider,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
} from "@coreui/react";
import type { ExecutionCommandType } from "@tradingdroid/frontends-types";
import { CSpinnerText } from "@tradingdroid/frontends-components";

import { useConfirmModal } from "@src/hooks/useConfirmModal";
import { useQuoteCurrency } from "@src/hooks/useQuoteCurrency";
import { useToastActions } from "@src/hooks/useToasts";
import { tdApi } from "@src/services/api/tdApi";
import { getApiErrorMessage } from "@src/utils/apiError";
import { END_EXECUTION_POLL_MAX_ATTEMPTS, EXECUTION_POLL_DELAY_MS } from "@src/utils/constants";
import { delay } from "@src/utils/dateUtils";

import { CapitalCommandForm } from "./CapitalCommandForm";
import { WithdrawCommandForm } from "./WithdrawCommandForm";

interface Props {
  executionId: number;
  exchangeName: string;
  symbolName: string;
  // Called once a recovery request has been sent, whether it committed or was refused: a refusal
  // means the row moved, and a success means it changed, so either way the screen is behind.
  // MUST return true only when a fresh execution was actually installed on the page. Anything else
  // withholds the Recovery item, because offering it over a row the page could not reload invites a
  // second attempt against whatever the execution has since become.
  onExecutionChanged: () => Promise<boolean>;
}

export const ExecutionCommandsDropdown: React.FC<Props> = ({
  executionId,
  exchangeName,
  symbolName,
  onExecutionChanged,
}) => {
  const [loadingCommand, setLoadingCommand] = useState<string | null>(null);
  // A refused command leaves this menu rendered from the row the server declined to act on. The
  // refresh is what replaces it, so when the refresh does not land the menu stays withheld rather
  // than re-opening over the stale row and inviting a second click on the state the first was
  // refused for. Latched until a refresh positively reports a fresh row.
  const [staleAfterRefresh, setStaleAfterRefresh] = useState(false);

  // Never throws: this runs from a finally, where a rejection would skip the loading reset and leave
  // the menu stuck on "Sending...". A refresh that rejects and one that reports no fresh row put the
  // operator in the same position, so both latch.
  const refreshExecution = async (): Promise<void> => {
    try {
      setStaleAfterRefresh((await onExecutionChanged()) !== true);
    } catch {
      setStaleAfterRefresh(true);
    }
  };
  const [pollingCommand, setPollingCommand] = useState<string | null>(null);
  const [capitalStatus, setCapitalStatus] = useState<string | null>(null);
  const [withdrawStatus, setWithdrawStatus] = useState<string | null>(null);
  const pollAbortRef = useRef(false);
  // Reserved synchronously on activation, before the confirm modal is awaited. `loadingCommand` is
  // only set after the operator confirms, so without this two quick activations both pass the
  // disabled check and open two confirmations.
  const reservedRef = useRef(false);
  const reserve = (): boolean => {
    if (reservedRef.current) return false;
    reservedRef.current = true;
    return true;
  };

  const quoteCurrency = useQuoteCurrency(exchangeName, symbolName);
  const { showToast } = useToastActions();
  const { confirmModal } = useConfirmModal();
  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      pollAbortRef.current = true;
    };
  }, []);

  const pollCommandStatus = useCallback(
    async (
      commandId: number,
      commandType: string,
      successMessage: string,
      options?: { showResultAlert?: boolean; skipNavigation?: boolean; successTitle?: string },
    ) => {
      setPollingCommand(commandType);
      pollAbortRef.current = false;
      let reachedTerminalState = false;

      for (let attempt = 0; attempt < END_EXECUTION_POLL_MAX_ATTEMPTS; attempt++) {
        if (pollAbortRef.current) break;

        await delay(EXECUTION_POLL_DELAY_MS);

        try {
          const command = await tdApi.getExecutionCommand(executionId, commandId);
          if (pollAbortRef.current) break;
          if (!command) continue;

          if (command.status === "completed") {
            reachedTerminalState = true;

            if (options?.showResultAlert && command.result) {
              const echoMessage = command.result.echo || JSON.stringify(command.result);
              showToast({
                type: "info",
                title: "Echo Response",
                message: echoMessage,
                timeout: 10000,
              });
            }

            showToast({
              type: "success",
              title:
                options?.successTitle ??
                (options?.skipNavigation ? "Command Complete" : "Execution Ended"),
              message: successMessage,
              timeout: 8000,
            });

            if (!options?.skipNavigation) {
              navigate("/executions");
            }
            break;
          } else if (command.status === "failed") {
            reachedTerminalState = true;
            showToast({
              type: "danger",
              title: "Command Failed",
              message: command.error_message || "Unknown error",
              timeout: 15000,
            });
            break;
          }
        } catch (err) {
          console.error("[ExecutionCommandsDropdown] Polling error:", err);
        }
      }

      if (!reachedTerminalState && !pollAbortRef.current) {
        showToast({
          type: "warning",
          title: `Command #${commandId} Timeout`,
          message: "Command is still processing. Check worker logs or try again later.",
          timeout: 15000,
        });
      }

      if (!pollAbortRef.current) {
        setPollingCommand(null);
      }
    },
    [executionId, showToast, navigate],
  );

  const sendCommand = async (
    commandType: ExecutionCommandType,
    options: {
      confirmTitle?: string;
      confirmMessage?: string;
      payload?: Record<string, unknown>;
      processingMessage: string;
      successMessage: string;
      successTitle?: string;
      showResultAlert?: boolean;
      skipNavigation?: boolean;
    },
  ) => {
    if (!reserve()) return;
    try {
      if (options.confirmMessage) {
        const { confirmed } = await confirmModal(options.confirmMessage, {
          title: options.confirmTitle,
        });
        if (!confirmed) return;
      }

      setLoadingCommand(commandType);
      const command = await tdApi.createExecutionCommand(executionId, {
        command_type_name: commandType,
        payload: options.payload,
      });

      if (!command?.id) {
        throw new Error("Command created but no ID returned");
      }

      showToast({
        type: "info",
        title: `Command #${command.id} Sent`,
        message: options.processingMessage,
        timeout: 5000,
      });

      pollCommandStatus(command.id, commandType, options.successMessage, {
        showResultAlert: options.showResultAlert,
        skipNavigation: options.skipNavigation,
        successTitle: options.successTitle,
      });
    } catch (err) {
      // Refusals such as an execution that has already ended, or an end-execution without its
      // acknowledgement, answer 422 with the reason in the body. Prefer that text over the
      // transport's bare status line.
      const message = getApiErrorMessage(err);
      showToast({
        type: "danger",
        title: "Command Failed",
        message: `Failed to send command: ${message}`,
        timeout: 8000,
      });
    } finally {
      setLoadingCommand(null);
      reservedRef.current = false;
    }
  };

  const isDisabled =
    loadingCommand !== null ||
    pollingCommand !== null ||
    capitalStatus !== null ||
    withdrawStatus !== null;

  // Same guard as handleRecovery: on this control `disabled` is only a stylesheet rule, so each
  // handler refuses on its own while a command is in flight.
  const handleEcho = () => {
    if (isDisabled) return;
    const message = window.prompt("Enter a message to echo (or leave empty for default):", "");
    if (message === null) return;

    sendCommand("echo", {
      payload: message ? { message } : undefined,
      processingMessage: "Testing round-trip communication...",
      successMessage: "Round-trip communication successful!",
      showResultAlert: true,
      skipNavigation: true,
    });
  };

  const handleRecovery = async () => {
    // Guarded here and not only by the item's `disabled`, because on this control that prop is not a
    // real barrier: CDropdownItem renders as an anchor, and CoreUI's CLink spreads the caller's
    // onClick over its own disabled-checking wrapper, so the wrapper never runs. What withholds a
    // disabled item is then `pointer-events: none` from the stylesheet — a presentation rule, not a
    // guarantee the handler will not be entered.
    if (isDisabled || staleAfterRefresh || !reserve()) return;

    const { confirmed } = await confirmModal(
      `Trigger recovery for execution #${executionId} on ${exchangeName}/${symbolName}?\n\nThis will force the orchestrator to re-acquire the lease and resume normal operation.`,
      { title: "Trigger Recovery" },
    );
    if (!confirmed) {
      reservedRef.current = false;
      return;
    }

    setLoadingCommand("recovery");
    try {
      const result = await tdApi.recoverExecution(executionId);
      if (result?.triggered) {
        showToast({
          type: "success",
          title: "Recovery Triggered",
          message: `Execution #${executionId} recovery initiated. Check Discord for status.${
            result.cleared_stop_request
              ? ` The pending "${result.cleared_stop_request}" stop request was cancelled.`
              : ""
          }`,
          timeout: 8000,
        });
      } else {
        showToast({
          type: "warning",
          title: "Recovery",
          message: "Recovery request sent but response was unexpected.",
          timeout: 8000,
        });
      }
    } catch (err) {
      // A refused recovery is a 409 whose body says what the row became. Show that rather than the
      // transport's bare status line.
      showToast({
        type: "danger",
        title: "Recovery Failed",
        message: getApiErrorMessage(err),
        timeout: 8000,
      });
    } finally {
      // Refresh on both paths, not only on the refusal: a success changed the row this menu was
      // rendered from, and a refusal means the row is not what it was rendered from either. The menu
      // stays withheld until the refresh confirms a fresh one.
      await refreshExecution();
      setLoadingCommand(null);
      reservedRef.current = false;
    }
  };

  // Confirming this warning is the operator's permission to leave positions and orders unmanaged, and
  // `leave_positions_open: true` is how the command carries that permission. sendCommand sends the
  // payload only after the confirmation resolves true, so a cancelled confirmation sends nothing.
  const handleEndOnly = () => {
    if (isDisabled) return;
    sendCommand("end-execution", {
      confirmTitle: "END EXECUTION ONLY",
      confirmMessage:
        "This will END the execution and stop managing positions and orders. No close is requested: any remaining positions and resting orders stay AS-IS on the exchange.\n\nThis applies even if the execution is currently flat.\n\nYou will need to manually manage your positions!\n\nOnly use this if you know what you're doing.\n\nAre you sure?",
      payload: { leave_positions_open: true },
      processingMessage: "Ending execution (positions will NOT be closed). Please wait...",
      successTitle: "Stop Command Completed",
      successMessage:
        "Stop command completed. No close was requested. Check the execution status and any remaining positions and resting orders.",
    });
  };

  const handleEndAndClose = () => {
    if (isDisabled) return;
    sendCommand("end-execution-and-close-market", {
      confirmTitle: "END EXECUTION & CLOSE ALL",
      confirmMessage:
        "This will:\n1. Close all open positions at market price\n2. End the execution\n\nAre you sure?",
      processingMessage: "Closing all positions at market and ending execution. Please wait...",
      successMessage: "All positions closed at market. Execution has ended.",
    });
  };

  const activeLabel =
    (pollingCommand && `${pollingCommand}...`) ||
    capitalStatus ||
    withdrawStatus ||
    (loadingCommand && "Sending...") ||
    null;

  return (
    <CDropdown autoClose="outside">
      <CDropdownToggle color="secondary" size="sm" disabled={isDisabled}>
        {activeLabel ? <CSpinnerText text={activeLabel} /> : "Commands"}
      </CDropdownToggle>
      <CDropdownMenu>
        <CDropdownItem className="cursor-pointer" onClick={handleEcho} disabled={isDisabled}>
          Echo Test
        </CDropdownItem>
        <CDropdownDivider />
        <CapitalCommandForm
          executionId={executionId}
          quoteCurrency={quoteCurrency}
          disabled={isDisabled}
          onStatusChange={setCapitalStatus}
        />
        <CDropdownDivider />
        <WithdrawCommandForm
          executionId={executionId}
          quoteCurrency={quoteCurrency}
          disabled={isDisabled}
          onStatusChange={setWithdrawStatus}
        />
        <CDropdownDivider />
        <CDropdownItem
          className="cursor-pointer"
          onClick={handleRecovery}
          disabled={isDisabled || staleAfterRefresh}
        >
          {staleAfterRefresh ? "Recovery (reload to enable)" : "Recovery"}
        </CDropdownItem>
        <CDropdownDivider />
        <CDropdownItem className="cursor-pointer" onClick={handleEndOnly} disabled={isDisabled}>
          End
        </CDropdownItem>
        <CDropdownDivider />
        <CDropdownItem className="cursor-pointer" onClick={handleEndAndClose} disabled={isDisabled}>
          End + Close
        </CDropdownItem>
      </CDropdownMenu>
    </CDropdown>
  );
};
